import React, { useState, useRef, useEffect } from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';
import MobileDialog from './MobileDialog';
import API from '../../services/api';

const DeleteTransactionDialog = ({
  open,
  onClose,
  transaction,
  onSuccess
}) => {
  const [password, setPassword] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState('');
  const isSubmittingRef = useRef(false);
  const errorRef = useRef(null);

  // Scroll to error when it appears
  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [error]);

  // Translate transaction type to Hebrew
  const getTypeLabel = (type) => {
    if (type === 'Payment') return 'תשלום';
    if (type === 'Charge') return 'חיוב';
    if (type === 'Credit') return 'זיכוי';
    return type;
  };

  const handleClose = () => {
    setPassword('');
    setError('');
    onClose();
  };

  const handleDelete = async () => {
    if (isSubmittingRef.current) return;
    if (!password || !transaction) {
      setError('יש להזין סיסמה');
      return;
    }

    isSubmittingRef.current = true;
    setIsDeleting(true);
    setError('');

    try {
      await API.delete('/transaction/delete/', {
        data: {
          transaction_id: transaction.TrID,
          password: password
        }
      });

      if (onSuccess) {
        onSuccess();
      }

      handleClose();
    } catch (err) {
      console.error('שגיאה במחיקת התנועה:', err);
      if (err.response && err.response.status === 401) {
        setError('סיסמה שגויה');
      } else {
        setError(
          err?.response?.data?.error ||
          err.message ||
          'שגיאה במחיקת התנועה'
        );
      }
    } finally {
      setIsDeleting(false);
      isSubmittingRef.current = false;
    }
  };

  if (!transaction) {
    return null;
  }

  return (
    <MobileDialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick' && !isDeleting) {
          handleClose();
        }
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ fontSize: '1.3rem', fontWeight: 'bold', color: '#d32f2f' }}>
        מחיקת תנועה
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {error && (
            <Alert ref={errorRef} severity="error" sx={{ fontWeight: 'bold' }}>{error}</Alert>
          )}
          <Alert severity="warning" sx={{ fontWeight: 'bold' }}>
            האם אתה בטוח שברצונך למחוק תנועה זו? פעולה זו אינה הפיכה ותשפיע על יתרת הלקוח.
          </Alert>
          <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
            <Typography><strong>סוג:</strong> {getTypeLabel(transaction.Type)}</Typography>
            <Typography><strong>סכום:</strong> {transaction.Amount}</Typography>
            <Typography><strong>לקוח:</strong> {transaction.Client?.ClientName || transaction.ClientName}</Typography>
          </Box>
          <TextField
            label="הזן סיסמה לאישור"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            fullWidth
            autoFocus
            sx={{ '& .MuiInputLabel-root': { fontWeight: 'bold' } }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={handleClose}
          color="inherit"
          disabled={isDeleting}
          sx={{ fontWeight: 'bold' }}
        >
          ביטול
        </Button>
        <Button
          onClick={handleDelete}
          variant="contained"
          color="error"
          disabled={isDeleting || !password}
          sx={{ fontWeight: 'bold', minWidth: 100 }}
        >
          {isDeleting ? <CircularProgress size={24} /> : 'מחק תנועה'}
        </Button>
      </DialogActions>
    </MobileDialog>
  );
};

export default DeleteTransactionDialog;
